import React, { useState } from 'react';
import { Box, Typography, IconButton, Stack, Snackbar, Alert } from '@mui/material';
import { format, isSameDay } from 'date-fns';
import { DndContext, useSensor, useSensors, PointerSensor, TouchSensor, DragOverlay, closestCorners } from '@dnd-kit/core';
import { useChoreLogic } from './useChoreLogic';
import TaskColumn from './TaskColumn';
import TaskCard from './TaskCard';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';




const PersonalCommandCenter = ({ userId, onBack }) => {
    const { kanbanBoard, moveTask, qualificationStatus, quotaStats, loading } = useChoreLogic(userId);
    const [activeId, setActiveId] = useState(null);
    const [filter, setFilter] = useState(null); // 'mandatory', 'daily', 'weekly', 'monthly'
    
    // Snackbar State
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const handleCloseSnackbar = () => setSnackbar(prev => ({ ...prev, open: false }));

    const handleFilterClick = (type) => {
        setFilter(prev => prev === type ? null : type);
    };

    // Filter Logic
    const filteredBoard = React.useMemo(() => {
        if (!filter) return kanbanBoard;

        const filterFn = (task) => {
            if (filter === 'mandatory') return task.type === 'fixed_daily';
            if (filter === 'daily') return task.type === 'db' && task.frequency === 'daily';
            if (filter === 'weekly') return (task.type === 'db' && task.frequency === 'weekly') || task.type === 'fixed_weekly';
            if (filter === 'monthly') return (task.type === 'db' && task.frequency === 'monthly') || task.type === 'fixed_monthly';
            return true;
        };

        return {
            todo: kanbanBoard.todo.filter(filterFn),
            doing: kanbanBoard.doing.filter(filterFn),
            done: kanbanBoard.done.filter(filterFn)
        };
    }, [kanbanBoard, filter]);

    // Helpers to find task across columns
    const allTasks = [...kanbanBoard.todo, ...kanbanBoard.doing, ...kanbanBoard.done];

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } })
    );

    const handleDragOver = (event) => {
        const { over } = event;
        if (!over) return;
        // Provide visual feedback for moving between columns (handled by dnd-kit default if sortable context is right)
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        setActiveId(null);
        if (!over) return;
        
        const taskId = active.id;
        const overId = over.id; 
        
        // Find Active Task Container
        let sourceColId = null;
        if (kanbanBoard.todo.find(t => t.id === taskId)) sourceColId = 'todo';
        else if (kanbanBoard.doing.find(t => t.id === taskId)) sourceColId = 'doing';
        else if (kanbanBoard.done.find(t => t.id === taskId)) sourceColId = 'done';
        
        if (!sourceColId) return;

        // Determine Dest Container & Index
        let destColId = null;
        let newIndex = 0;

        if (['todo', 'doing', 'done'].includes(overId)) {
            // Dropped on a column container (empty space or end)
            destColId = overId;
            newIndex = kanbanBoard[destColId].length; // End of list
        } else {
            // Dropped on another Item
            const overTask = allTasks.find(t => t.id === overId);
            if (overTask) {
                // Find which column overTask is in
                if (kanbanBoard.todo.find(t => t.id === overId)) destColId = 'todo';
                else if (kanbanBoard.doing.find(t => t.id === overId)) destColId = 'doing';
                else if (kanbanBoard.done.find(t => t.id === overId)) destColId = 'done';
                
                // Find index of overTask
                const overIndex = kanbanBoard[destColId].findIndex(t => t.id === overId);
                newIndex = overIndex;

                if (sourceColId === destColId) {
                     // Same column logic
                     newIndex = overIndex;
                } else {
                    newIndex = overIndex;
                }
            }
        }

        if (sourceColId && destColId) {
            const result = await moveTask(taskId, sourceColId, destColId, newIndex);
            if (result && result.restoredDate) {
                const restoredDateObj = result.restoredDate.toDate 
                    ? result.restoredDate.toDate() 
                    : new Date(result.restoredDate);
                
                const isToday = isSameDay(restoredDateObj, new Date());

                if (!isToday) {
                    const dateStr = format(restoredDateObj, 'EEE MMM d');
                    setSnackbar({
                        open: true,
                        severity: 'info',
                        message: `Already counted for ${dateStr}!`
                    });
                }
            }
        }
    };

    if (loading) return <Typography variant="h2">Loading...</Typography>;

    const userName = userId.charAt(0).toUpperCase() + userId.slice(1);

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <IconButton onClick={onBack} size="large" sx={{ p: 2, bgcolor: 'white', boxShadow: 2, '&:hover': { bgcolor: '#edf2f7' } }}>
                    <ArrowBackIcon fontSize="large" />
                </IconButton>
                <Typography variant="h3" fontWeight="bold" sx={{ flex: 1, color: '#2d3748' }}>
                    {userName}'s Command Center
                </Typography>
                
                {/* Quota Indicators */}
                {/* Quota Indicators */}
                 <Stack direction="row" spacing={1} sx={{ mr: 2 }}>
                    <Box 
                        onClick={() => handleFilterClick('mandatory')}
                        sx={{ 
                            px: 2, py: 0.5, 
                            bgcolor: quotaStats?.mandatory.current >= quotaStats?.mandatory.target ? '#bfdbfe' : '#fed7d7', 
                            color: quotaStats?.mandatory.current >= quotaStats?.mandatory.target ? '#1e3a8a' : '#822727',
                            borderRadius: 2, fontWeight: 'bold', fontSize: '0.9rem',
                            display: 'flex', alignItems: 'center', gap: 0.5,
                            cursor: 'pointer',
                            border: filter === 'mandatory' ? '2px solid #2b6cb0' : '2px solid transparent',
                            opacity: filter && filter !== 'mandatory' ? 0.5 : 1
                        }}
                    >
                        Required {quotaStats?.mandatory.current}/{quotaStats?.mandatory.target}
                    </Box>
                    <Box 
                        onClick={() => handleFilterClick('daily')}
                        sx={{ 
                            px: 2, py: 0.5, 
                            bgcolor: quotaStats?.daily.current >= quotaStats?.daily.target ? '#c6f6d5' : '#fed7d7', 
                            color: quotaStats?.daily.current >= quotaStats?.daily.target ? '#22543d' : '#822727',
                            borderRadius: 2, fontWeight: 'bold', fontSize: '0.9rem',
                            display: 'flex', alignItems: 'center', gap: 0.5,
                            cursor: 'pointer',
                            border: filter === 'daily' ? '2px solid #276749' : '2px solid transparent',
                            opacity: filter && filter !== 'daily' ? 0.5 : 1
                        }}
                    >
                        Daily {quotaStats?.daily.current}/{quotaStats?.daily.target}
                    </Box>
                    <Box 
                        onClick={() => handleFilterClick('weekly')}
                        sx={{ 
                            px: 2, py: 0.5, 
                            bgcolor: '#bee3f8', 
                            color: '#2c5282',
                            borderRadius: 2, fontWeight: 'bold', fontSize: '0.9rem',
                            cursor: 'pointer',
                            border: filter === 'weekly' ? '2px solid #2c5282' : '2px solid transparent',
                            opacity: filter && filter !== 'weekly' ? 0.5 : 1
                        }}
                    >
                        Weekly {quotaStats?.weekly.current}/{quotaStats?.weekly.target}
                    </Box>
                    <Box 
                        onClick={() => handleFilterClick('monthly')}
                        sx={{ 
                            px: 2, py: 0.5, 
                            bgcolor: '#e9d8fd', 
                            color: '#553c9a',
                            borderRadius: 2, fontWeight: 'bold', fontSize: '0.9rem',
                            cursor: 'pointer',
                            border: filter === 'monthly' ? '2px solid #553c9a' : '2px solid transparent',
                            opacity: filter && filter !== 'monthly' ? 0.5 : 1
                        }}
                    >
                         Monthly {quotaStats?.monthly.current}/{quotaStats?.monthly.target}
                    </Box>
                </Stack>
                
                {/* Qualification Light */}
                <Box sx={{ 
                    px: 4, py: 1.5, 
                    borderRadius: 8, 
                    bgcolor: qualificationStatus.qualified ? '#48bb78' : '#f56565',
                    color: 'white',
                    boxShadow: 4,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                    transition: 'all 0.5s',
                    transform: qualificationStatus.qualified ? 'scale(1.05)' : 'none'
                }}>
                    <Box sx={{ 
                        width: 40, height: 40, borderRadius: '50%', 
                        bgcolor: qualificationStatus.qualified ? '#9ae6b4' : '#820000',
                        border: '4px solid white',
                        boxShadow: '0 0 10px rgba(0,0,0,0.2)'
                    }} />
                    <Box>
                        <Typography variant="h4" fontWeight="900" sx={{ letterSpacing: 1 }}>
                            {qualificationStatus.qualified ? 'SCREENS ' : 'NO SCREENS'}
                        </Typography>
                        {!qualificationStatus.qualified && qualificationStatus.missing && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.9 }}>
                                Missing: {qualificationStatus.missing}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Stack>

            {/* Kanban */}
            <DndContext 
                sensors={sensors} 
                collisionDetection={closestCorners}
                onDragStart={(event) => setActiveId(event.active.id)} 
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
            >
                <Stack direction="row" spacing={3} sx={{ flex: 1, minHeight: 0 }}>
                    <TaskColumn id="todo" title="TO DO" tasks={filteredBoard.todo} color="#4299e1" />
                    <TaskColumn id="doing" title="DOING" tasks={filteredBoard.doing} color="#ecc94b" />
                    <TaskColumn id="done" title="DONE" tasks={filteredBoard.done} color="#48bb78" />
                </Stack>
                <DragOverlay>
                    {activeId ? (
                        <TaskCard 
                            task={allTasks.find(t => t.id === activeId)} 
                            isOverlay 
                        />
                    ) : null}
                </DragOverlay>
            </DndContext>

            
            <Snackbar 
                open={snackbar.open} 
                autoHideDuration={6000} 
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%', fontSize: '1.1rem', fontWeight: 'bold' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default PersonalCommandCenter;
